/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package analytics

import models.TimeSeriesPoint
import kotlin.math.*

/**
 * Detects spending anomalies and patterns in financial time series data
 */
class AnomalyDetector {
    
    /**
     * Detect anomalies in time series data using statistical methods
     * @param points Time series data points
     * @param threshold Standard deviation threshold for anomaly detection (default 2.0)
     * @return List of detected anomalies
     */
    fun detectAnomalies(
        points: List<TimeSeriesPoint>, 
        threshold: Double = 2.0
    ): List<Anomaly> {
        if (points.size < 3) {
            return emptyList() // Need minimum data for meaningful statistics
        }
        
        val values = points.map { it.value }
        val mean = values.average()
        val stdDev = calculateStandardDeviation(values, mean)
        
        if (stdDev == 0.0) {
            return emptyList() // No variation means no anomalies
        }
        
        val anomalies = mutableListOf<Anomaly>()
        
        points.forEachIndexed { index, point ->
            val zScore = abs((point.value - mean) / stdDev)
            
            if (zScore > threshold) {
                val type = when {
                    point.value > mean -> "HIGH_SPENDING"
                    else -> "LOW_SPENDING"
                }
                
                anomalies.add(
                    Anomaly(
                        index = index,
                        value = point.value,
                        type = type,
                        deviation = zScore,
                        expectedValue = mean
                    )
                )
            }
        }
        
        return anomalies
    }
    
    /**
     * Detect patterns in time series data including seasonality, trend changes, and volatility
     * @param points Time series data points
     * @return Detected patterns
     */
    fun detectPatterns(points: List<TimeSeriesPoint>): SpendingPatterns {
        if (points.isEmpty()) {
            return SpendingPatterns()
        }
        
        val seasonalPattern = detectSeasonalPattern(points)
        val trendChange = detectTrendChange(points)
        val volatilePeriods = detectVolatilePeriods(points)
        
        return SpendingPatterns(
            seasonalPattern = seasonalPattern,
            trendChange = trendChange,
            volatilePeriods = volatilePeriods
        )
    }
    
    private fun detectSeasonalPattern(points: List<TimeSeriesPoint>): SeasonalPattern? {
        if (points.size < 12) {
            return null // Need at least 12 points for seasonal detection
        }
        
        val values = points.map { it.value }
        
        // Try different period lengths (2 to 6)
        for (period in 2..6) {
            if (points.size < period * 3) continue // Need at least 3 cycles
            
            val correlations = mutableListOf<Double>()
            
            // Calculate correlation for each cycle
            for (offset in 0 until period) {
                val cycleValues = mutableListOf<Double>()
                var index = offset
                while (index < values.size) {
                    cycleValues.add(values[index])
                    index += period
                }
                
                if (cycleValues.size >= 3) {
                    // Check if values at this offset are consistently higher/lower
                    val cycleMean = cycleValues.average()
                    val overallMean = values.average()
                    val deviation = abs(cycleMean - overallMean) / overallMean
                    correlations.add(deviation)
                }
            }
            
            // Check if we have a strong seasonal pattern
            val maxDeviation = correlations.maxOrNull() ?: 0.0
            if (maxDeviation > 0.3) { // 30% deviation threshold
                return SeasonalPattern(
                    period = period,
                    confidence = min(0.95, maxDeviation * 2), // Scale to confidence
                    strength = maxDeviation
                )
            }
        }
        
        return null
    }
    
    private fun detectTrendChange(points: List<TimeSeriesPoint>): TrendChange? {
        if (points.size < 6) {
            return null // Need minimum points for trend detection
        }
        
        val values = points.map { it.value }
        var maxChange = 0.0
        var changePoint = -1
        
        // Check for significant changes in moving average
        for (i in 3 until values.size - 2) {
            val beforeAvg = values.subList(max(0, i - 3), i).average()
            val afterAvg = values.subList(i, min(values.size, i + 3)).average()
            val change = abs(afterAvg - beforeAvg)
            
            if (change > maxChange) {
                maxChange = change
                changePoint = i
            }
        }
        
        // Check if change is significant
        val overallStdDev = calculateStandardDeviation(values, values.average())
        if (maxChange > overallStdDev * 1.5) {
            return TrendChange(
                changePoint = changePoint,
                magnitude = maxChange,
                direction = if (values[changePoint] > values[max(0, changePoint - 1)]) "UP" else "DOWN"
            )
        }
        
        return null
    }
    
    private fun detectVolatilePeriods(points: List<TimeSeriesPoint>): List<VolatilePeriod>? {
        if (points.size < 5) {
            return null
        }
        
        val values = points.map { it.value }
        val periods = mutableListOf<VolatilePeriod>()
        val windowSize = 3
        
        // Calculate rolling standard deviation
        val rollingVolatility = mutableListOf<Double>()
        for (i in 0..values.size - windowSize) {
            val window = values.subList(i, i + windowSize)
            val windowMean = window.average()
            val windowStdDev = calculateStandardDeviation(window, windowMean)
            rollingVolatility.add(windowStdDev)
        }
        
        if (rollingVolatility.isEmpty()) {
            return null
        }
        
        // Find periods of high volatility
        val overallVolatility = rollingVolatility.average()
        val volatilityThreshold = overallVolatility * 2.0
        
        var inVolatilePeriod = false
        var periodStart = 0
        
        rollingVolatility.forEachIndexed { index, volatility ->
            if (volatility > volatilityThreshold && !inVolatilePeriod) {
                inVolatilePeriod = true
                periodStart = index
            } else if (volatility <= volatilityThreshold && inVolatilePeriod) {
                inVolatilePeriod = false
                periods.add(
                    VolatilePeriod(
                        startIndex = periodStart,
                        endIndex = index + windowSize - 1,
                        averageVolatility = rollingVolatility.subList(periodStart, index).average()
                    )
                )
            }
        }
        
        // Handle case where volatility continues to the end
        if (inVolatilePeriod) {
            periods.add(
                VolatilePeriod(
                    startIndex = periodStart,
                    endIndex = values.size - 1,
                    averageVolatility = rollingVolatility.subList(periodStart, rollingVolatility.size).average()
                )
            )
        }
        
        return if (periods.isNotEmpty()) periods else null
    }
    
    private fun calculateStandardDeviation(values: List<Double>, mean: Double): Double {
        if (values.size <= 1) return 0.0
        
        val variance = values.sumOf { (it - mean).pow(2) } / values.size
        return sqrt(variance)
    }
}

/**
 * Represents a detected anomaly in spending data
 */
data class Anomaly(
    val index: Int,
    val value: Double,
    val type: String, // "HIGH_SPENDING" or "LOW_SPENDING"
    val deviation: Double, // Number of standard deviations from mean
    val expectedValue: Double
)

/**
 * Collection of detected spending patterns
 */
data class SpendingPatterns(
    val seasonalPattern: SeasonalPattern? = null,
    val trendChange: TrendChange? = null,
    val volatilePeriods: List<VolatilePeriod>? = null
)

/**
 * Represents a seasonal spending pattern
 */
data class SeasonalPattern(
    val period: Int, // Number of time units in the cycle
    val confidence: Double, // 0.0 to 1.0
    val strength: Double // Magnitude of seasonal variation
)

/**
 * Represents a significant change in spending trend
 */
data class TrendChange(
    val changePoint: Int, // Index where trend changed
    val magnitude: Double, // Size of the change
    val direction: String // "UP" or "DOWN"
)

/**
 * Represents a period of high volatility in spending
 */
data class VolatilePeriod(
    val startIndex: Int,
    val endIndex: Int,
    val averageVolatility: Double
)